import { btn, input } from './components';
import { h, span } from './dom';
import './suggestBox.css';

/**
 * The app's own type-ahead combobox (D122): an input, a status line and up to five two-line rows.
 * It owns the look and the timing (debounce, minimum length, generation guard, keyboard, offline
 * line); the caller owns the data. It never calls a provider itself: `fetch` and `pick` are the
 * only way out, so the Route tab (browser Places library) and the lead form (D110 proxy) can
 * share it by swapping those two functions.
 */

export interface SuggestRow {
  main: string;
  secondary: string;
}

/** Why a fetch produced no rows: no connection, the provider refused the key, or anything else. */
export interface SuggestFailure {
  kind: 'offline' | 'denied' | 'error';
}

export interface SuggestCopy {
  searching: string;
  noMatch: (query: string) => string;
  offline: string;
  error: string;
  picking: string;
}

export interface SuggestBoxOpts<R extends SuggestRow> {
  /** The field's accessible name when no visible label points at it. */
  ariaLabel?: string;
  /** The field's id, for a caller that pairs a visible `<label for>` with it (then omit ariaLabel). */
  id?: string;
  placeholder: string;
  listLabel: string;
  copy: SuggestCopy;
  fetch: (query: string) => Promise<readonly R[] | SuggestFailure>;
  /** Resolves true when the pick is done; false puts the rows back (the caller says why). */
  pick: (row: R) => Promise<boolean>;
  onDenied?: () => void;
  minChars?: number;
  debounceMs?: number;
  maxRows?: number;
}

export interface SuggestBox {
  root: HTMLElement;
  input: HTMLInputElement;
  reset: () => void;
  dispose: () => void;
  /** The caller's resting line: shown whenever the box has no message of its own; '' clears it.
   *  `warn` colours that resting line only; the box's own messages keep `--muted` (CAY-115). */
  setStatus: (text: string, tone?: 'warn') => void;
  setDisabled: (disabled: boolean) => void;
}

const isFailure = <R>(r: readonly R[] | SuggestFailure): r is SuggestFailure => !Array.isArray(r);

/** The Google Maps logo pair (dark and light theme), hidden until provider content is on screen.
 *  The box shows one under its rows; the lead form shows another under its preview (D110, D122). */
export const googleAttribution = (extraClass?: string): HTMLElement => {
  const logo = (tone: 'dark' | 'light', file: string) =>
    h('img', { class: `suggest__logo suggest__logo--${tone}`, src: `/attribution/${file}`, alt: 'Google Maps' });
  return h(
    'div',
    { class: ['suggest__attribution', extraClass].filter(Boolean).join(' '), hidden: true, translate: 'no' },
    logo('dark', 'GoogleMaps_Logo_White.svg'),
    logo('light', 'GoogleMaps_Logo_DarkGray.svg'),
  );
};

export const suggestBox = <R extends SuggestRow>(opts: SuggestBoxOpts<R>): SuggestBox => {
  const min = opts.minChars ?? 3;
  const wait = opts.debounceMs ?? 300;
  const max = opts.maxRows ?? 5;
  const listId = `suggest-${crypto.randomUUID()}`;
  let gen = 0;
  let disposed = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let rows: readonly R[] = [];
  let rowsFor = '';
  let active = -1;
  let searching = false;
  let pickFirst = false;
  let lastFailed = false; // the last request for the current text failed and nothing is on screen
  let retryArmed = false; // one automatic retry per offline → online transition

  const status = h('p', { class: 'suggest__status', role: 'status', 'aria-live': 'polite' });
  const list = h('div', { id: listId, class: 'suggest__list', role: 'listbox', 'aria-label': opts.listLabel, hidden: true });
  const attribution = googleAttribution();
  const field = input({
    type: 'search',
    class: 'input--search suggest__input',
    placeholder: opts.placeholder,
    ariaLabel: opts.ariaLabel,
    autocapitalize: 'words',
    autocomplete: 'off',
    enterkeyhint: 'search',
    maxlength: 200,
    onInput: () => changed(),
  });
  if (opts.id) field.id = opts.id;
  field.setAttribute('role', 'combobox');
  field.setAttribute('aria-autocomplete', 'list');
  field.setAttribute('aria-controls', listId);
  field.setAttribute('aria-expanded', 'false');

  const text = () => field.value.trim();
  // One status line, two layers (D122 amendment, CAY-83): the caller's resting text (the lead
  // form's ledger line, Route's "Finding the spot…") and the box's own transient message
  // (searching, no match, offline, error, picking), which shows over it. Every place the box
  // finishes its own message goes through `settle()`, so the resting text comes back. While rows
  // are open the resting text steps aside (spec §3.5: the line is empty over results, CAY-124).
  let resting = '';
  let restingTone: 'warn' | undefined;
  let message: string | null = null;
  const paint = () => {
    status.textContent = message ?? (rows.length ? '' : resting);
    status.classList.toggle('suggest__status--warn', message === null && !rows.length && resting !== '' && restingTone === 'warn');
  };
  const say = (t: string) => {
    message = t;
    paint();
  };
  const settle = () => {
    message = null;
    paint();
  };
  const setStatus = (t: string, tone?: 'warn') => {
    resting = t;
    restingTone = tone;
    paint();
  };

  const render = () => {
    list.replaceChildren(
      ...rows.map((row, i) => {
        const option = btn([span('suggest__main', row.main), row.secondary ? span('suggest__sub', row.secondary) : null], {
          variant: 'subtle',
          class: 'suggest__row',
          onClick: () => void choose(row),
        });
        option.id = `${listId}-${i}`;
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', String(i === active));
        option.tabIndex = -1;
        return option;
      }),
    );
    const open = rows.length > 0;
    list.hidden = !open;
    attribution.hidden = !open;
    field.setAttribute('aria-expanded', String(open));
    paint();
    if (active >= 0) field.setAttribute('aria-activedescendant', `${listId}-${active}`);
    else field.removeAttribute('aria-activedescendant');
  };

  const collapse = () => {
    rows = [];
    rowsFor = '';
    active = -1;
    render();
  };

  const reset = () => {
    gen++;
    clearTimeout(timer);
    timer = undefined;
    searching = false;
    pickFirst = false;
    lastFailed = false;
    settle();
    collapse();
  };

  const fail = (kind: SuggestFailure['kind']) => {
    collapse();
    if (kind === 'denied') {
      settle();
      opts.onDenied?.();
      return;
    }
    lastFailed = true;
    if (kind === 'offline') {
      if (navigator.onLine === false) retryArmed = true;
      say(opts.copy.offline);
    } else say(opts.copy.error);
  };

  const search = async (query: string) => {
    timer = undefined;
    const mine = gen;
    if (navigator.onLine === false) {
      // A request now would only fail; wait for the connection instead. An Enter typed before this
      // must not carry over: the reconnect retry only shows rows, it never picks one by itself.
      pickFirst = false;
      retryArmed = true;
      lastFailed = true;
      collapse();
      say(opts.copy.offline);
      return;
    }
    searching = true;
    say(opts.copy.searching);
    const result = await opts.fetch(query);
    if (disposed || mine !== gen) return;
    searching = false;
    if (isFailure(result)) {
      pickFirst = false;
      return fail(result.kind);
    }
    lastFailed = false;
    rows = result.slice(0, max);
    rowsFor = query;
    active = -1;
    if (rows.length) settle();
    else say(opts.copy.noMatch(query));
    render();
    if (pickFirst && rows[0]) void choose(rows[0]);
    pickFirst = false;
  };

  function changed() {
    gen++;
    clearTimeout(timer);
    timer = undefined;
    searching = false;
    pickFirst = false;
    lastFailed = false;
    active = -1;
    const query = text();
    if (query.length < min) {
      settle();
      collapse();
      return;
    }
    render(); // previous rows stay on screen until the new answer arrives
    timer = setTimeout(() => void search(query), wait);
  }

  async function choose(row: R) {
    gen++; // a late answer must not reopen the list over the pick
    clearTimeout(timer);
    timer = undefined;
    searching = false;
    const back = { rows, rowsFor };
    const mine = gen;
    field.value = row.main;
    collapse();
    say(opts.copy.picking);
    const done = await opts.pick(row);
    if (disposed || mine !== gen) return;
    settle();
    if (done) return;
    // Put the text back with the rows, so the list matches the field again and Enter (no
    // highlight) picks the first row on the retry (spec §3.4).
    field.value = back.rowsFor;
    rows = back.rows;
    rowsFor = back.rowsFor;
    render();
  }

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      // First Escape closes the list and keeps the text (a search field would clear it); a late
      // answer must not reopen it. With nothing open, Escape keeps its native clear.
      if (rows.length || searching || timer !== undefined) e.preventDefault();
      reset();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!rows.length) return;
      active = (active + (e.key === 'ArrowDown' ? 1 : -1) + rows.length) % rows.length;
      render();
      return;
    }
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const query = text();
    if (active >= 0 && rows[active]) void choose(rows[active]!);
    else if (rows[0] && rowsFor === query) void choose(rows[0]);
    else if (timer !== undefined) {
      // typed it all and hit Go before the debounce: search now, take the first match
      clearTimeout(timer);
      pickFirst = true;
      void search(query);
    } else if (searching) pickFirst = true;
  };
  field.addEventListener('keydown', onKey);

  const onOffline = () => {
    retryArmed = true;
  };
  const onOnline = () => {
    if (!retryArmed || disposed) return;
    retryArmed = false;
    if (!lastFailed || field.disabled || text().length < min) return;
    clearTimeout(timer);
    pickFirst = false; // the rep tapped nothing since the drop: show rows, do not spend on a pick
    void search(text());
  };
  window.addEventListener('offline', onOffline);
  window.addEventListener('online', onOnline);

  const root = h('div', { class: 'suggest' }, field, status, list, attribution);
  return {
    root,
    input: field,
    reset,
    setStatus,
    setDisabled: (disabled: boolean) => {
      field.disabled = disabled;
      if (disabled) reset();
    },
    dispose: () => {
      disposed = true;
      reset();
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
    },
  };
};
